import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';

import { MatSort } from '@angular/material/sort';
import { BehaviorSubject, Subscription } from 'rxjs';
import { UserV2 } from '../../shared/models';
import { UsersService } from '../../users.service';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-users-in-role',
  templateUrl: './users-in-role.component.html',
  styleUrls: ['./users-in-role.component.scss'],
})
export class UsersInRoleComponent implements OnInit, OnDestroy {
  @Input() roleId: number;
  @Input() readonly: boolean;
  @Input() refreshUsersBS: BehaviorSubject<boolean>;
  @Output() usersSelection: EventEmitter<Set<number>> = new EventEmitter();

  maxHeight = 500;
  displayedColumns = ['selection', 'email', 'name', 'nRoles'];
  dataSource = new TableVirtualScrollDataSource([]);

  selectedUserSet = new Set<number>();
  readonly MAX_ROLES_TO_ATTACH = 5;
  searchCtrl = new FormControl('');

  private users = [];
  private subscriptions: Subscription = new Subscription();

  /**
   * Allows to have a full responsive table by taking the available height. The overflow of the table will be visible by using a scroll.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.maxHeight = Math.max(document.getElementById('right-side-content').clientHeight - 235, 500);
  }

  sortActive = 'email';
  sortDirection: 'asc' | 'desc' = 'asc';
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(private usersService: UsersService, @Inject('UsersRolesFactory') private usersRolesService) {
    this.fetchData();
    this.subscriptions.add(this.searchCtrl.valueChanges.pipe(debounceTime(300)).subscribe(this.filterUsers));
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.refreshUsersBS.subscribe(value => {
        if (value) {
          this.fetchData();
        }
      })
    );
  }

  private fetchData() {
    this.selectedUserSet = new Set<number>();
    this.usersService.getUsersV2().subscribe({
      next: (users: UserV2[]) => {
        users.forEach(user => {
          user['rolesSet'] = new Set<number>();
          if (user.roles?.length) {
            const userRolesIds = user.roles.map(({ id }) => id);
            userRolesIds.forEach(id => user['rolesSet'].add(id));
            if (userRolesIds.includes(this.roleId)) {
              this.selectedUserSet.add(user.id);
            }
          }
        });
        this.usersSelection.emit(this.selectedUserSet);
        this.dataSource = new TableVirtualScrollDataSource(users);
        this.dataSource.sort = this.sort;
        this.users = users;
        this.filterUsers(this.searchCtrl.value);
        this.onResize();
      },
    });
  }

  selectedUsersChange({ checked }, userId) {
    if (checked) {
      this.selectedUserSet.add(userId);
    } else {
      this.selectedUserSet.delete(userId);
    }
    this.usersSelection.emit(this.selectedUserSet);
  }

  sortData($event) {
    const { active, direction } = $event;
    this.sortActive = active;
    this.sortDirection = direction;
    const listSorted = this.dataSource.data.sort((a: UserV2, b: UserV2) => {
      switch (active) {
        case 'email':
        case 'name':
          return (a[active] || '').localeCompare(b[active]);
        case 'nRoles':
          return (a.roles || []).length - (b.roles || []).length;
        case 'selection': {
          const aValue = this.selectedUserSet.has(a.id) ? 1 : 0;
          const bValue = this.selectedUserSet.has(b.id) ? 1 : 0;
          return aValue - bValue;
        }
        default:
          break;
      }
    });
    if (direction === 'desc') {
      listSorted.reverse();
    }

    this.dataSource.sortData(listSorted, this.sort);
  }

  redirectToUserEdit(user: UserV2) {
    this.usersRolesService.redirectToUserEdit(null, user, true);
  }

  /**
   * Allows to clear the search filter.
   */
  clearSearch() {
    this.searchCtrl.reset();
  }

  private filterUsers = (value: string): void => {
    const usersFiltered = this.users.filter(({ email }) => !value || new RegExp(value, 'i').test(email));
    this.dataSource = new TableVirtualScrollDataSource(usersFiltered);
    this.dataSource.sort = this.sort;
  };

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
